import React, { Fragment, useEffect, useState } from "react";
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  FormControl,
  FormLabel,
  RadioGroup,
  Radio,
  FormControlLabel,
  Checkbox,
  Select,
  MenuItem,
  InputLabel,
  Grid,
  FormHelperText,
  Paper,
  Stack,
  CircularProgress,
  Backdrop
} from "@mui/material";
import { ArrowBack as ArrowBackIcon, CloudUpload as CloudUploadIcon } from "@mui/icons-material";
import axios from "axios";
import { useParams, useNavigate, Link } from "react-router-dom";
import Swal from "sweetalert2";
import { BASE_URL } from "../../variables/api";
import useCheckSuperAdmin from "../../hooks/useCheckSuperAdmin";

const ViewForm = (props) => {
  const checkIfAdmin = useCheckSuperAdmin();
  const navigate = useNavigate();
  const { id } = useParams();

  let emaildomain = localStorage.getItem("emaildomain");
  if (checkIfAdmin) {
    let selected_school = localStorage.getItem("selected_school");
    try {
      emaildomain = JSON.parse(selected_school)?.value;
    } catch (e) {
      console.error("Error parsing selected_school", e);
    }
  }

  const [formData, setFormData] = useState({
    name: "",
    FormArr: [],
    description: "",
  });
  
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(true);
  const [add, setAdd] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    axios.post(`${BASE_URL}/surveys/getsurvey.php`, { id })
      .then((res) => {
        if (res.data && res.data.data) {
          setFormData((prevstate) => ({
            ...prevstate,
            name: res.data.data.name,
            FormArr: res.data.data.questions || [],
            description: res.data.data.description,
          }));
        }
      })
      .catch((err) => {
        console.error("Error fetching survey:", err);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [add, id]);

  // Validation Effect
  useEffect(() => {
    const checkValidation = () => {
      if (!formData.FormArr || formData.FormArr.length === 0) return;

      let isValid = true;

      for (const question of formData.FormArr) {
        if (question.required) {
          if (question.type.value === "checkbox" || question.type.value === "radio" || question.type.value === "linescale") {
            // Check if at least one option is selected
            const hasSelection = question.options.some(opt => opt.response === true || opt.response === "true");
            if (!hasSelection) {
              isValid = false;
              break;
            }
          } else if (question.type.value === "select") {
             if (!question.response || !question.response.value) {
               isValid = false;
               break;
             }
          } else if (question.type.value === "file") {
             if (!question.response) {
               isValid = false;
               break;
             }
          } else {
            // Text, textarea, etc.
            if (!question.response || question.response.trim() === "") {
              isValid = false;
              break;
            }
          }
        }
      }
      setIsSubmitDisabled(!isValid);
    };

    checkValidation();
  }, [formData]);


  const handleChange = (event, questionId) => {
    const value = event.target.value;
    setFormData((prevstate) => {
      const FormArrCopy = [...prevstate.FormArr];
      const questionIndex = FormArrCopy.findIndex(q => q.id === questionId);
      if (questionIndex !== -1) {
        FormArrCopy[questionIndex].response = value;
      }
      return { ...prevstate, FormArr: FormArrCopy };
    });
  };

  const handleChangeselect = (event, questionId) => {
    const value = event.target.value; // This is the value string
    setFormData((prevstate) => {
      const FormArrCopy = [...prevstate.FormArr];
      const questionIndex = FormArrCopy.findIndex(q => q.id === questionId);
      if (questionIndex !== -1) {
        const question = FormArrCopy[questionIndex];
        // Find the full option object
        const selectedOption = question.options.find(opt => opt.value === value);
        
        // Set response to the object as expected by backend/legacy logic
        question.response = selectedOption ? { value: selectedOption.value, label: selectedOption.value } : null;
        
        // Also update options response status (legacy compatibility)
        question.options.forEach(opt => {
            opt.response = (opt.value === value);
        });
      }
      return { ...prevstate, FormArr: FormArrCopy };
    });
  };

  const handleChangeFile = (e, questionId) => {
    if (e.target.files === null || e.target.files.length === 0) return;
    const selectedFile = e.target.files[0];
    
    setFormData((prevstate) => {
      const FormArrCopy = [...prevstate.FormArr];
      const questionIndex = FormArrCopy.findIndex(q => q.id === questionId);
      if (questionIndex !== -1) {
        FormArrCopy[questionIndex].response = selectedFile;
      }
      return { ...prevstate, FormArr: FormArrCopy };
    });
  };

  const handleChangeDynamicOptions = (event, questionId, optionIndex) => {
    const checked = event.target.checked;
    const type = event.target.type; // checkbox or radio

    setFormData((prevstate) => {
      const FormArrCopy = [...prevstate.FormArr];
      const questionIndex = FormArrCopy.findIndex(q => q.id === questionId);
      
      if (questionIndex !== -1) {
        const question = FormArrCopy[questionIndex];
        
        if (question.type.value === "radio" || question.type.value === "linescale") {
            // For radio, uncheck others
            question.options.forEach((opt, idx) => {
                opt.response = (idx === optionIndex) ? true : false;
            });
        } else {
            // For checkbox
            question.options[optionIndex].response = checked;
        }
      }
      return { ...prevstate, FormArr: FormArrCopy };
    });
  };

  const submitHandler = (e) => {
    e.preventDefault();
    Swal.showLoading();
    const surveyData = new FormData();

    surveyData.append("formData", JSON.stringify(formData));
    surveyData.append("id", id);
    
    formData.FormArr.forEach((question) => {
      if (question.type.value === "file" && question.response) {
        surveyData.append(`${question.id}`, question.response);
      }
    });

    axios
      .post(`${BASE_URL}/surveys/addresponse.php`, surveyData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      })
      .then((res) => {
        Swal.fire("Response Submitted Successfully", "", "success");
        setFormData((prevstate) => ({ ...prevstate, FormArr: [] }));
        setAdd(!add);
        if(props.setPending) props.setPending(!props.pending);
        navigate("/");
      })
      .catch((err) => {
        console.error(err);
        Swal.fire("Error", "Failed to submit response", "error");
      });
  };

  return (
    <Box sx={{ py: { xs: 1, sm: 2 } }}>
      {/* Loading Overlay */}
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loading}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
          <CircularProgress color="inherit" />
          <Typography variant="h6">Loading Survey...</Typography>
        </Box>
      </Backdrop>

      <Container maxWidth="md">
        <Button
          component={Link}
          to="/"
          startIcon={<ArrowBackIcon />}
          sx={{ mb: 3 }}
          variant="outlined"
        >
          Back to Home
        </Button>

        <Box sx={{ textAlign: "center", mb: 4 }}>
          {emaildomain && (
            <img
              src={`/assets/${emaildomain}/logo.png`}
              alt="logo"
              style={{ maxWidth: "200px", height: "auto" }}
              onError={(e) => {e.target.style.display='none'}}
            />
          )}
        </Box>

        <Paper elevation={3} sx={{ p: 4, mb: 4, borderRadius: 2 }}>
          <Typography variant="h4" align="center" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>
            {formData.name}
          </Typography>
          <Box
            sx={{ mt: 2, typography: 'body1' }}
            dangerouslySetInnerHTML={{ __html: formData?.description }}
          />
        </Paper>

        <form onSubmit={submitHandler}>
          <Stack spacing={3}>
            {formData.FormArr.map((question, qIndex) => (
              <Card key={question.id} sx={{ borderRadius: 2, boxShadow: 1 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    {question.Question} {question.required && <span style={{ color: "red" }}>*</span>}
                  </Typography>
                  {question.description && (
                    <Typography variant="body2" color="textSecondary" paragraph>
                      {question.description}
                    </Typography>
                  )}

                  <Box sx={{ mt: 2 }}>
                    {/* Text Inputs */}
                    {(question.type.value === "text" || question.type.value === "email" || question.type.value === "number" || question.type.value === "date") && (
                      <TextField
                        fullWidth
                        type={question.type.value}
                        variant="outlined"
                        required={question.required}
                        value={question.response || ""}
                        onChange={(e) => handleChange(e, question.id)}
                        placeholder={`Enter ${question.Question}`}
                      />
                    )}

                    {/* Paragraph */}
                    {question.type.value === "paragraph" && (
                      <TextField
                        fullWidth
                        multiline
                        rows={4}
                        variant="outlined"
                        required={question.required}
                        value={question.response || ""}
                        onChange={(e) => handleChange(e, question.id)}
                        placeholder="Enter your answer"
                      />
                    )}

                    {/* Select */}
                    {question.type.value === "select" && (
                      <FormControl fullWidth required={question.required}>
                        <InputLabel>Select Option</InputLabel>
                        <Select
                          value={question.response?.value || ""}
                          label="Select Option"
                          onChange={(e) => handleChangeselect(e, question.id)}
                        >
                          {question.options.map((opt, idx) => (
                            <MenuItem key={idx} value={opt.value}>
                              {opt.value}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}

                    {/* Checkbox */}
                    {question.type.value === "checkbox" && (
                      <FormControl required={question.required} component="fieldset">
                        <Stack>
                          {question.options.map((opt, optIndex) => (
                            <FormControlLabel
                              key={optIndex}
                              control={
                                <Checkbox
                                  checked={opt.response === true}
                                  onChange={(e) => handleChangeDynamicOptions(e, question.id, optIndex)}
                                />
                              }
                              label={opt.value}
                            />
                          ))}
                        </Stack>
                      </FormControl>
                    )}

                    {/* Radio */}
                    {question.type.value === "radio" && (
                      <FormControl required={question.required} component="fieldset">
                        <RadioGroup
                          value={question.options.findIndex(o => o.response === true) !== -1 ? question.options.findIndex(o => o.response === true) : ""}
                        >
                          {question.options.map((opt, optIndex) => (
                            <FormControlLabel
                              key={optIndex}
                              value={optIndex}
                              control={<Radio onChange={(e) => handleChangeDynamicOptions(e, question.id, optIndex)} checked={opt.response === true} />}
                              label={opt.value}
                            />
                          ))}
                        </RadioGroup>
                      </FormControl>
                    )}

                    {/* Line Scale (treated as Radio for now, maybe horizontal) */}
                    {question.type.value === "linescale" && (
                       <FormControl required={question.required} component="fieldset">
                       <RadioGroup
                         row
                         value={question.options.findIndex(o => o.response === true) !== -1 ? question.options.findIndex(o => o.response === true) : ""}
                       >
                         {question.options.map((opt, optIndex) => (
                           <FormControlLabel
                             key={optIndex}
                             value={optIndex}
                             control={<Radio onChange={(e) => handleChangeDynamicOptions(e, question.id, optIndex)} checked={opt.response === true} />}
                             label={opt.value}
                             labelPlacement="bottom"
                             sx={{ mx: 1 }}
                           />
                         ))}
                       </RadioGroup>
                     </FormControl>
                    )}

                    {/* File */}
                    {question.type.value === "file" && (
                      <Box>
                        <Button
                          variant="contained"
                          component="label"
                          startIcon={<CloudUploadIcon />}
                        >
                          Upload File
                          <input
                            type="file"
                            hidden
                            onChange={(e) => handleChangeFile(e, question.id)}
                          />
                        </Button>
                        {question.response && (
                          <Typography variant="body2" sx={{ mt: 1 }}>
                            Selected: {question.response.name}
                          </Typography>
                        )}
                      </Box>
                    )}
                  </Box>
                </CardContent>
              </Card>
            ))}

            <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                size="large"
                disabled={isSubmitDisabled}
                sx={{ px: 5, py: 1.5, fontSize: '1.1rem' }}
              >
                Submit Response
              </Button>
            </Box>
          </Stack>
        </form>
      </Container>
    </Box>
  );
};

export default ViewForm;
